import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SectionRowRule } from '../../../../models/scan-testing.model';

// One section entry the user is defining. `rows` are the specific rows to test;
// empty means the whole section. `rowInput` backs the add-a-row field.
interface SectionEntry {
  section: string;
  rows: string[];
  rowInput: string;
}

// Configures a Section & Row rule. The tester defines which sections and rows to
// test — these are NOT preloaded from the API, since the point is to seed test
// rows for locations the tester chooses. Each selection seeds an extended row.
@Component({
  selector: 'app-sr-section-row-modal',
  templateUrl: './sr-section-row-modal.component.html',
  standalone: false,
  styleUrls: ['./sr-modal-shared.scss']
})
export class SrSectionRowModalComponent implements OnInit {
  @Input() initial?: SectionRowRule;
  @Output() confirmed = new EventEmitter<SectionRowRule>();
  @Output() cancelled = new EventEmitter<void>();

  entries: SectionEntry[] = [];
  sectionInput = '';

  ngOnInit(): void {
    this.entries = (this.initial?.selections ?? []).map((sel) => ({
      section: sel.section,
      rows: [...(sel.rows ?? [])],
      rowInput: ''
    }));
  }

  addSection(): void {
    const section = this.sectionInput.trim();
    this.sectionInput = '';
    if (!section) { return; }
    if (this.entries.some((e) => e.section.toLowerCase() === section.toLowerCase())) { return; }
    this.entries = [...this.entries, { section, rows: [], rowInput: '' }];
  }

  removeSection(entry: SectionEntry): void {
    this.entries = this.entries.filter((e) => e !== entry);
  }

  addRow(entry: SectionEntry): void {
    const row = entry.rowInput.trim();
    entry.rowInput = '';
    if (!row) { return; }
    if (entry.rows.some((r) => r.toLowerCase() === row.toLowerCase())) { return; }
    entry.rows = [...entry.rows, row];
  }

  removeRow(entry: SectionEntry, row: string): void {
    entry.rows = entry.rows.filter((r) => r !== row);
  }

  private newSeedKey(): string {
    return `sr_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  onConfirm(): void {
    const selections = this.entries
      .filter((e) => e.section.trim())
      .map((e) => ({ section: e.section.trim(), rows: [...e.rows] }));
    this.confirmed.emit({
      type: 'section_row',
      selections,
      seedKey: this.initial?.seedKey || this.newSeedKey()
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  trackEntry(_idx: number, entry: SectionEntry): string { return entry.section; }
  trackRow(_idx: number, row: string): string { return row; }
}
